/**
 * EditAccessInbox — the lock holder's popover of incoming edit-access requests
 * for the current site (#82).
 *
 * Mounted by SiteLockControls only when the viewer IS the current lock holder
 * (`uid === site.lockedByUid`). Lists OPEN requests live via
 * `subscribeEditAccessRequests` (Firestore onSnapshot off the flat
 * `edit_access_requests` collection — no 5-minute notification poll lag), and
 * lets the holder:
 *   - Approve → TRANSFERS the lock to the requester (one holder at a time).
 *   - Deny   → request closed, lock stays with the holder.
 *
 * Both actions go through the store (approveEditRequest / denyEditRequest),
 * which round-trips the server router so the cross-uid site write + notify +
 * email happen under the admin SDK.
 *
 * Zustand discipline: subscribe to scalar action roots only; request list is
 * local component state fed by the onSnapshot callback (never derived in a
 * selector).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KeyRound, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { SiteEntity } from '../../types/site';
import type { EditAccessRequest } from '../../types/editAccessRequest';
import { useStore } from '../../store';
import { subscribeEditAccessRequests } from '../../services/editAccessClient';

export interface EditAccessInboxProps {
  site: SiteEntity;
}

export function EditAccessInbox({ site }: EditAccessInboxProps): React.ReactElement {
  const approveEditRequest = useStore((s) => s.approveEditRequest);
  const denyEditRequest = useStore((s) => s.denyEditRequest);

  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState<EditAccessRequest[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const holderUid = site.lockedByUid ?? null;

  // Live subscription to OPEN requests addressed to this holder.
  useEffect(() => {
    if (!holderUid) {
      setRequests([]);
      return;
    }
    const unsub = subscribeEditAccessRequests(
      holderUid,
      (next) => setRequests(next.filter((r) => r.siteId === site.id)),
      (err) => {
        // Non-fatal: the bell + email still surface the request. Log only.
        console.warn('[EditAccessInbox] subscription error', err);
      },
    );
    return () => unsub();
  }, [holderUid, site.id]);

  // Close popover on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pendingCount = requests.length;

  const handleApprove = async (req: EditAccessRequest) => {
    setBusyId(req.id);
    try {
      await approveEditRequest(req.id);
      toast.success(`Edit access transferred to ${req.requestedByName || req.requestedByEmail}.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeny = async (req: EditAccessRequest) => {
    setBusyId(req.id);
    try {
      await denyEditRequest(req.id);
      toast.success(`Request from ${req.requestedByName || req.requestedByEmail} denied.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to deny request');
    } finally {
      setBusyId(null);
    }
  };

  const timeAgo = useMemo(
    () => (iso: string) => {
      const ts = new Date(iso).getTime();
      if (Number.isNaN(ts)) return '';
      const diff = Date.now() - ts;
      if (diff < 0) return 'just now';
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    },
    [],
  );

  return (
    <div ref={ref} className="relative shrink-0" data-testid="edit-access-inbox">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="edit-access-inbox-trigger"
        className="relative inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-border-subtle bg-card/30 text-[10px] uppercase tracking-widest text-text-secondary hover:bg-state-hover hover:text-text-primary transition-colors"
        title="Edit-access requests"
      >
        <KeyRound className="w-3.5 h-3.5" />
        Requests
        {pendingCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-danger text-text-primary text-[9px] font-bold flex items-center justify-center rounded-full">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-background border border-border shadow-lg z-50 max-h-96 flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-secondary">
              Edit-access requests {pendingCount > 0 && `(${pendingCount})`}
            </span>
          </div>
          <div className="overflow-y-auto flex-1">
            {requests.length === 0 ? (
              <div className="p-6 text-center text-[10px] text-text-secondary uppercase tracking-widest">
                No pending requests
              </div>
            ) : (
              requests.map((req) => (
                <div
                  key={req.id}
                  className="px-3 py-2.5 border-b border-border-subtle"
                  data-testid="edit-access-request-row"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-text-primary truncate">
                      {req.requestedByName || req.requestedByEmail}
                    </span>
                    <span className="text-[9px] text-text-secondary shrink-0">{timeAgo(req.requestedAt)}</span>
                  </div>
                  {req.message && (
                    <p className="text-[9px] mt-0.5 text-text-secondary italic break-words">“{req.message}”</p>
                  )}
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      type="button"
                      onClick={() => handleApprove(req)}
                      disabled={busyId !== null}
                      data-testid="edit-access-approve"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-primary/50 bg-primary/10 text-[9px] uppercase tracking-widest text-primary hover:bg-primary/20 disabled:opacity-50 transition-colors"
                      title="Approve — transfers the lock to this user"
                    >
                      {busyId === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeny(req)}
                      disabled={busyId !== null}
                      data-testid="edit-access-deny"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-border-subtle bg-card/30 text-[9px] uppercase tracking-widest text-text-secondary hover:bg-state-hover hover:text-text-primary disabled:opacity-50 transition-colors"
                    >
                      <X className="w-3 h-3" />
                      Deny
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
